import * as Sentry from '@sentry/browser'
import { useUnleashContext } from '@unleash/proxy-client-react'
import cookie from 'js-cookie'
import { useCallback, useEffect, useRef, useState } from 'react'
import ReactGA from 'react-ga4'
import { useLocation } from 'react-router-dom'

import { CookieBannerVariantEnum, initCookies } from '@web-apps/utils-shared'

import { LoadingIndicator, Theme } from '@web-apps/ui-shared'
import { useTranslation } from 'react-i18next'
import {
  ErrorApp,
  NeedsNativeAuth,
  NoAccess,
  PendingVerification,
} from '../../../components'
import { PageLayout } from '../../../layouts'
import { getEnvironmentConfigs } from '../../../services/utility'
import { config } from '../../../utils/helpers/config.helpers'
import { getImpersonatedZezamId } from '../../../utils/helpers/impersonation.helpers'
import {
  clearRNWebViewCredential,
  getRNWebViewCredentialMode,
  isRNWebView,
  RN_CREDENTIAL_CHANGED_EVENT,
} from '../../../utils/helpers/rnWebView.helpers'
import { PostRegistrationPage } from '../RegistrationPage'
import { useAuthState } from '../useAuthState'
import { NonAuthenticated } from './NonAuthenticated'

export const Auth = () => {
  const { t } = useTranslation(['app', 'auth'])
  const updateContext = useUnleashContext()
  const location = useLocation()

  const [resolveKey, setResolveKey] = useState(0)
  const resolveAgain = useCallback(() => setResolveKey((key) => key + 1), [])

  const cookieConsentBannerContainerRef = useRef<HTMLDivElement>(null)
  const [cookiesStatus, setCookiesStatus] = useState(
    cookie.get('cookieconsent_status')
  )

  const isEmbedded = isRNWebView()
  const impersonatedZezamId = getImpersonatedZezamId()

  const { state, session, userId, metapicUserId } = useAuthState({
    isEmbedded,
    embeddedCredentialMode: getRNWebViewCredentialMode(),
    impersonatedUserId: impersonatedZezamId,
    resolveKey,
  })

  useEffect(() => {
    window.addEventListener(RN_CREDENTIAL_CHANGED_EVENT, resolveAgain)
    return () =>
      window.removeEventListener(RN_CREDENTIAL_CHANGED_EVENT, resolveAgain)
  }, [resolveAgain])

  const handleCredentialRejected = useCallback(() => {
    if (isEmbedded) {
      clearRNWebViewCredential()
      return
    }

    resolveAgain()
  }, [isEmbedded, resolveAgain])

  const isPending = state === 'loading'

  useEffect(() => {
    if (!cookieConsentBannerContainerRef.current || isPending) return

    initCookies({
      containerElement: cookieConsentBannerContainerRef.current,
      cookieBannerVariant: CookieBannerVariantEnum.COOKIE,
      messages: {
        message: t('app:cookie_consent.message'),
        allow: t('app:cookie_consent.allow'),
        deny: t('app:cookie_consent.deny'),
        link: t('app:cookie_consent.link'),
        linkText: t('app:cookie_consent.link_text'),
        cookieBannerText: t('app:cookie_consent.title'),
      },
      cookieDomain: getEnvironmentConfigs().cookieDomain,
      host: window.location.host,
      onCookieStatusChange: (status: string) => setCookiesStatus(status),
      styles: {
        buttonBackgroundColor: Theme.CookieBanner.buttonBackgroundColor,
        mainButtonTextColor: Theme.CookieBanner.mainButtonTextColor,
        mainButtonBackgroundColor: Theme.CookieBanner.mainButtonBackgroundColor,
        buttonTextColor: Theme.CookieBanner.buttonTextColor,
        titleTextColor: Theme.CookieBanner.titleTextColor,
      },
    })
  }, [t, isPending])

  useEffect(() => {
    if (userId) {
      updateContext({
        userId,
      })
      Sentry.setUser({ id: userId, ip_address: undefined })
    }
  }, [userId, updateContext])

  useEffect(() => {
    if (userId && config.googleAnalyticsId && cookiesStatus === 'allow') {
      ReactGA.initialize([
        {
          trackingId: config.googleAnalyticsId,
          gaOptions: {
            userId,
          },
        },
      ])
    }
  }, [userId, cookiesStatus])

  useEffect(() => {
    ReactGA.send('pageview')
  }, [location])

  switch (state) {
    case 'loading':
      return <LoadingIndicator size={100} isCentered />

    case 'impersonation-failed':
    case 'error':
      return <ErrorApp />

    case 'needs-native-auth':
      return <NeedsNativeAuth />

    case 'suspended':
      return <NoAccess />

    case 'needs-zezam-account':
      return (
        <div ref={cookieConsentBannerContainerRef}>
          <PostRegistrationPage
            sessionData={{ metapicUserId }}
            onZezamUserRegister={resolveAgain}
            onCredentialRejected={handleCredentialRejected}
          />
        </div>
      )

    case 'needs-metapic-account':
    case 'pending-verification':
      return <PendingVerification />

    case 'anonymous':
      return (
        <div ref={cookieConsentBannerContainerRef}>
          <NonAuthenticated />
        </div>
      )

    case 'ready':
      return session && userId ? (
        <PageLayout userId={userId} session={session} />
      ) : null
  }
}
